/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { ColorName, Colors, ColorsCSS, TypographyPDL } from '../../../theme';

import type { LiteralUnion } from '../../../types';

import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
extend([a11yPlugin]);

export const Styles = labelStyles('TagLabel', {
  root: (backgroundColor: string, textColor: string) => {
    return css`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      border-radius: var(--zds-radius-small, 30px);

      background-color: ${backgroundColor};
      color: ${textColor};

      &[data-size='small'] {
        height: auto;
        padding: var(--zds-space-0, 0) var(--zds-space-4, 8px);
        ${TypographyPDL('--zds-small-print-2', 'smallPrint1Bold')}
      }

      &[data-size='medium'] {
        height: auto;
        padding: var(--zds-space-0, 3px) var(--zds-space-4, 8px);
        ${TypographyPDL('--zds-body-2', 'smallPrint1Bold')}
      }

      /* TODO: PDL variant hardcoded for use in UniversalLayout, update values in v8 */
      &[data-color='simple'] {
        padding: var(--zds-space-0, 0) var(--zds-space-4, 4px);
        background-color: transparent;
        color: var(--zds-text-weakest, #6f6765);
        border: 1px solid var(--zds-stroke-default, #b5b2aa);
        border-radius: var(--zds-radius-small, 3px);
        font: var(--zds-small-print-2, 500 13px/20px Inter);
      }

      &[data-color='gray'] {
        background-color: var(--zds-gray-warm-9, ${Colors.neutral700});
        color: var(--zds-text-inverted, ${Colors.neutral100});
      }

      &[data-color='white'] {
        background-color: var(--zds-prime-white, ${Colors.neutral100});
        color: var(--zds-text-stronger, ${Colors.neutral800});
      }

      &[data-color='info'] {
        background-color: var(--zds-ui-primary-weaker, ${Colors.blue10});
        color: var(--zds-text-stronger, ${Colors.neutral800});
      }

      &[data-color='success'] {
        background-color: var(
          --zds-status-success-weaker,
          ${Colors.success100}
        );
        color: var(--zds-text-default, ${Colors.neutral800});
      }

      &[data-color='warning'] {
        background-color: var(
          --zds-status-warning-weaker,
          ${Colors.warning100}
        );
        color: var(--zds-text-default, ${Colors.neutral800});
      }

      &[data-color='error'] {
        background-color: var(--zds-status-error-weaker, ${Colors.error100});
        color: var(--zds-text-default, ${Colors.neutral800});
      }

      &[data-color='peach'] {
        background-color: ${ColorsCSS.peach};
        color: ${ColorsCSS.neutral800};
      }

      &[data-color='neutral300'] {
        background-color: ${ColorsCSS.neutral300};
        color: ${ColorsCSS.neutral800};
      }

      &[data-color='bolt'] {
        background-color: ${ColorsCSS.bolt};
        color: ${ColorsCSS.neutral800};
      }

      &[data-color='cream'] {
        background-color: ${ColorsCSS.cream};
        color: ${ColorsCSS.neutral800};
      }

      &[data-color='success500'] {
        background-color: ${ColorsCSS.success500};
        color: ${ColorsCSS.neutral100};
      }

      &[data-color='lavender'] {
        background-color: ${ColorsCSS.lavender};
        color: ${ColorsCSS.neutral800};
      }
    `;
  },
});

const tagLabelColors = [
  'simple',
  'gray',
  'white',
  'info',
  'success',
  'warning',
  'error',
  'peach',
  'neutral300',
  'bolt',
  'cream',
  'success500',
  'lavender',
] as const;

type TagLabelColorName = (typeof tagLabelColors)[number];

type Props = {
  /**
   * The color variant of the label.  Any design system color can be used.
   * @default 'gray'
   * */
  color?: LiteralUnion<TagLabelColorName, ColorName>;
  /**
   * Content to render inside the label. Generally this is plain text,
   * but other content such as icons or Avatars may also be rendered.
   */
  children: React.ReactNode;
  /**
   * The size of `TagLabel`.
   * @default 'medium'
   */
  size?: 'small' | 'medium';
  /**
   * Optional property for overriding styles with CSS-in-JS or defining a custom class on the top-level Select element.
   * Use with caution as the DOM structure of the component may change.
   */
  className?: string;
};

/**
 * Renders a tag-like component.
 */
export const TagLabel = ({
  children,
  color = 'gray',
  size = 'medium',
  className,
}: Props) => {
  let backgroundColor: ColorName = 'neutral700';
  let textColor: ColorName = 'neutral100';

  // If the provided color option was a predefined color choice...
  if (tagLabelColors.find((tagColor) => tagColor === color)) {
    switch (color) {
      case 'gray':
        backgroundColor = 'neutral700';
        textColor = 'neutral100';
        break;
      case 'white':
        backgroundColor = 'neutral100';
        textColor = 'neutral800';
        break;
      case 'info':
        backgroundColor = 'blue10';
        textColor = 'neutral800';
        break;
      case 'success':
        backgroundColor = 'success100';
        textColor = 'neutral800';
        break;
      case 'warning':
        backgroundColor = 'warning100';
        textColor = 'neutral800';
        break;
      case 'error':
        backgroundColor = 'error100';
        textColor = 'neutral800';
        break;
      case 'peach':
        backgroundColor = 'peach';
        textColor = 'neutral800';
        break;
      case 'neutral300':
        backgroundColor = 'neutral300';
        textColor = 'neutral800';
        break;
      case 'bolt':
        backgroundColor = 'bolt';
        textColor = 'neutral800';
        break;
      case 'cream':
        backgroundColor = 'cream';
        textColor = 'neutral800';
        break;
      case 'success500':
        backgroundColor = 'success500';
        textColor = 'neutral100';
        break;
      case 'lavender':
        backgroundColor = 'lavender';
        textColor = 'neutral800';
        break;
    }
  }
  // If the provided color option is a custom color, calculate the correct text color.
  else {
    backgroundColor = color as ColorName;

    const lightTextContrast = colord(backgroundColor).contrast(
      Colors.neutral100
    );
    const darkTextContrast = colord(backgroundColor).contrast(
      Colors.neutral800
    );

    if (lightTextContrast > darkTextContrast) {
      textColor = 'neutral100';
    } else {
      textColor = 'neutral800';
    }
  }

  return (
    <span
      css={Styles.root(Colors[backgroundColor], Colors[textColor])}
      data-size={size}
      className={className}
      data-color={color}
      data-zds
    >
      {children}
    </span>
  );
};
